class PasCartController {
  constructor(root) {
    this.root = root;
    this.panel = root.querySelector('[role="dialog"]');
    this.lines = root.querySelector('[data-cart-lines]');
    this.footer = root.querySelector('[data-cart-footer]');
    this.status = root.querySelector('[data-cart-status]');
    this.recommendation = root.querySelector('[data-cart-recommendation]');
    this.lastFocus = null;
    this.busy = false;
    this.routesRoot = window.Shopify?.routes?.root || '/';
    document.addEventListener('click', (event) => this.handleClick(event));
    document.addEventListener('submit', (event) => this.handleSubmit(event));
    document.addEventListener('keydown', (event) => this.handleKeydown(event));
  }

  endpoint(path) {
    return `${this.routesRoot}${path}`.replace(/\/+/g, '/');
  }

  async handleClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const opener = target.closest('[data-cart-open]');
    if (opener) { event.preventDefault(); await this.open(opener); return; }
    if (target.closest('[data-cart-close]')) { this.close(); return; }
    const change = target.closest('[data-cart-change]');
    if (change) {
      event.preventDefault();
      await this.change(change.dataset.lineKey, Number(change.dataset.quantity));
      return;
    }
    const suggestion = target.closest('[data-cart-suggestion-add]');
    if (suggestion) {
      event.preventDefault();
      await this.addVariant(suggestion.dataset.variantId, 1, suggestion);
    }
  }

  async handleSubmit(event) {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form || form.dataset.noAjax === 'true') return;
    const action = new URL(form.action || '', window.location.origin);
    if (!action.pathname.endsWith('/cart/add')) return;
    event.preventDefault();
    if (this.busy) return;
    const button = event.submitter instanceof HTMLButtonElement ? event.submitter : form.querySelector('[type="submit"]');
    const restoreTarget = form.closest('[data-quick-view]') ? window.PasQuickView?.lastFocus : (button || form);
    const previousDisabled = button?.disabled;
    if (button) button.disabled = true;
    this.setBusy(true, 'Adding to cart…');
    try {
      const response = await fetch(this.endpoint('cart/add.js'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'This item could not be added.');
      }
      await this.refresh();
      document.dispatchEvent(new CustomEvent('pas:cart-added'));
      await this.open(restoreTarget);
      this.announce('Added to cart.');
    } catch (error) {
      this.announce(error instanceof Error ? error.message : 'This item could not be added.');
    } finally {
      this.setBusy(false);
      if (button) button.disabled = Boolean(previousDisabled);
    }
  }

  async addVariant(id, quantity, button) {
    if (!id || this.busy) return;
    this.setBusy(true, 'Adding to cart…');
    if (button) button.disabled = true;
    try {
      const response = await fetch(this.endpoint('cart/add.js'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity }] }),
      });
      if (!response.ok) throw new Error('This item could not be added.');
      await this.refresh();
      this.announce('Added to cart.');
    } catch (error) {
      this.announce(error instanceof Error ? error.message : 'This item could not be added.');
    } finally {
      this.setBusy(false);
      if (button) button.disabled = false;
    }
  }

  async open(trigger) {
    if (!this.root.classList.contains('is-open')) this.lastFocus = trigger || document.activeElement;
    this.root.hidden = false;
    this.root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('pas-lock-scroll');
    requestAnimationFrame(() => { this.root.classList.add('is-open'); this.panel.focus(); });
    await this.refresh();
  }

  close() {
    if (this.root.hidden) return;
    this.root.classList.remove('is-open');
    this.root.setAttribute('aria-hidden', 'true');
    window.setTimeout(() => { if (!this.root.classList.contains('is-open')) this.root.hidden = true; }, 500);
    if (!document.querySelector('[data-quick-view].is-open')) document.documentElement.classList.remove('pas-lock-scroll');
    this.lastFocus?.focus?.();
  }

  async refresh() {
    try {
      const response = await fetch(this.endpoint('cart.js'), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart could not be refreshed.');
      const cart = await response.json();
      this.render(cart);
      this.updateCounts(cart.item_count);
      void this.loadRecommendation(cart);
      return cart;
    } catch (error) {
      this.announce(error instanceof Error ? error.message : 'Cart could not be refreshed.');
      return null;
    }
  }

  async change(key, quantity) {
    if (!key || this.busy) return;
    this.setBusy(true, 'Updating cart…');
    try {
      const response = await fetch(this.endpoint('cart/change.js'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, quantity || 0) }),
      });
      if (!response.ok) throw new Error('Cart could not be updated.');
      const cart = await response.json();
      this.render(cart);
      this.updateCounts(cart.item_count);
      void this.loadRecommendation(cart);
      this.announce(quantity > 0 ? 'Quantity updated.' : 'Item removed.');
    } catch (error) {
      this.announce(error instanceof Error ? error.message : 'Cart could not be updated.');
    } finally {
      this.setBusy(false);
    }
  }

  money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: currency || 'USD' }).format(Number(cents || 0) / 100);
    } catch {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  render(cart) {
    this.lines.replaceChildren();
    this.footer.hidden = cart.item_count === 0;
    this.root.querySelector('[data-cart-subtotal]').textContent = this.money(cart.total_price, cart.currency);
    if (!cart.items.length) {
      const empty = document.createElement('div');
      empty.className = 'pas-cart-drawer__empty';
      const copy = document.createElement('p');
      copy.textContent = 'Your cart is empty.';
      const link = document.createElement('a');
      link.href = this.root.dataset.continueUrl || '/collections/all';
      link.textContent = 'Continue shopping';
      empty.append(copy, link);
      this.lines.append(empty);
      this.recommendation.hidden = true;
      return;
    }

    const list = document.createElement('ul');
    list.className = 'pas-cart-drawer__items';
    cart.items.forEach((item) => list.append(this.renderLine(item, cart.currency)));
    this.lines.append(list);
  }

  renderLine(item, currency) {
    const row = document.createElement('li');
    row.className = 'pas-cart-drawer__item';
    if (item.image) {
      const imageLink = document.createElement('a');
      imageLink.href = item.url;
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = '';
      image.width = 88;
      image.height = 88;
      image.loading = 'lazy';
      imageLink.append(image);
      row.append(imageLink);
    }
    const content = document.createElement('div');
    content.className = 'pas-cart-drawer__item-copy';
    const title = document.createElement('a');
    title.className = 'pas-cart-drawer__item-title';
    title.href = item.url;
    title.textContent = item.product_title;
    content.append(title);
    if (item.variant_title && item.variant_title !== 'Default Title') {
      const variant = document.createElement('p');
      variant.className = 'pas-cart-drawer__variant';
      variant.textContent = item.variant_title;
      content.append(variant);
    }
    const controls = document.createElement('div');
    controls.className = 'pas-cart-drawer__item-controls';
    const quantity = document.createElement('div');
    quantity.className = 'pas-cart-drawer__quantity';
    quantity.append(
      this.changeButton('−', `Decrease quantity of ${item.product_title}`, item.key, Math.max(0, item.quantity - 1)),
      Object.assign(document.createElement('span'), { textContent: String(item.quantity) }),
      this.changeButton('+', `Increase quantity of ${item.product_title}`, item.key, item.quantity + 1),
    );
    const price = document.createElement('strong');
    price.textContent = this.money(item.final_line_price, currency);
    controls.append(quantity, price);
    content.append(controls);
    const remove = this.changeButton('Remove', `Remove ${item.product_title}`, item.key, 0);
    remove.className = 'pas-cart-drawer__remove';
    content.append(remove);
    row.append(content);
    return row;
  }

  changeButton(text, label, key, quantity) {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = text;
    button.setAttribute('aria-label', label);
    button.dataset.cartChange = '';
    button.dataset.lineKey = key;
    button.dataset.quantity = String(quantity);
    return button;
  }

  async loadRecommendation(cart) {
    this.recommendation.hidden = true;
    this.recommendation.replaceChildren();
    const first = cart.items[0];
    if (!first?.product_id) return;
    try {
      const response = await fetch(`${this.endpoint('recommendations/products.json')}?product_id=${encodeURIComponent(first.product_id)}&limit=4&intent=related`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const data = await response.json();
      const inCart = new Set(cart.items.map((item) => Number(item.product_id)));
      const product = data.products?.find((candidate) => !inCart.has(Number(candidate.id)) && candidate.available);
      const variant = product?.variants?.find((candidate) => candidate.available);
      if (!product || !variant) return;

      const eyebrow = document.createElement('p');
      eyebrow.className = 'pas-cart-drawer__recommendation-label';
      eyebrow.textContent = 'You might also like';
      const row = document.createElement('div');
      row.className = 'pas-cart-drawer__recommendation-row';
      if (product.featured_image) {
        const image = document.createElement('img');
        image.src = product.featured_image;
        image.alt = '';
        image.width = 64;
        image.height = 64;
        image.loading = 'lazy';
        row.append(image);
      }
      const link = document.createElement('a');
      link.href = product.url;
      link.textContent = product.title;
      const add = document.createElement('button');
      add.type = 'button';
      add.textContent = 'Add';
      add.dataset.cartSuggestionAdd = '';
      add.dataset.variantId = variant.id;
      row.append(link, add);
      this.recommendation.append(eyebrow, row);
      this.recommendation.hidden = false;
    } catch {
      /* Recommendations are optional. */
    }
  }

  updateCounts(count) {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
    document.querySelectorAll('[data-cart-open]').forEach((button) => {
      button.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`);
    });
  }

  setBusy(busy, message = '') {
    this.busy = busy;
    this.root.classList.toggle('is-busy', busy);
    this.root.querySelectorAll('button').forEach((button) => { button.disabled = busy; });
    if (message) this.announce(message);
  }

  announce(message) {
    if (this.status) this.status.textContent = message;
  }

  handleKeydown(event) {
    if (!this.root.classList.contains('is-open')) return;
    if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
    if (event.key !== 'Tab') return;
    const focusable = [...this.panel.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])')];
    if (!focusable.length) { event.preventDefault(); this.panel.focus(); return; }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
}

const pasCartRoot = document.querySelector('[data-cart-drawer]');
if (pasCartRoot) window.PasCart = new PasCartController(pasCartRoot);
